import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { m } from 'motion/react';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { RISE } from './motion';

/**
 * Las superficies del sistema: tarjeta, sección y panel.
 *
 * Antes cada pantalla escribía a mano `rounded-lg border border-line bg-raised
 * p-4`, con seis variantes de lo mismo repartidas por el producto. Aquí se
 * decide una vez, y de paso se gana la elevación al pasar por encima, que es lo
 * que hace que una lista larga se sienta manipulable en lugar de impresa.
 */
const card = cva('border transition-[border-color,box-shadow,transform] duration-fast ease-out-soft', {
  variants: {
    tone: {
      plain: 'border-line bg-paper',
      raised: 'border-line bg-raised shadow-sm',
      muted: 'border-transparent bg-muted',
      brand: 'border-primary/25 bg-linear-115 from-primary-pale to-raised',
      dashed: 'border-dashed border-line bg-transparent',
    },
    size: { sm: 'rounded-md p-3', md: 'rounded-lg p-5', lg: 'rounded-xl p-7' },
    interactive: { true: 'cursor-pointer hover:-translate-y-px hover:border-primary/45 hover:shadow-md', false: '' },
  },
  defaultVariants: { tone: 'raised', size: 'md', interactive: false },
});

export type CardProps = VariantProps<typeof card> & HTMLAttributes<HTMLDivElement>;

export function Card({ tone, size, interactive, className, ...props }: CardProps) {
  return <div className={cn(card({ tone, size, interactive }), className)} {...props} />;
}

/** Una tarjeta que entra sola cuando aparece, dentro de una lista escalonada. */
export function MotionCard({ tone, size, interactive, className, ...props }: CardProps) {
  return (
    <m.div variants={RISE} className={cn(card({ tone, size, interactive }), className)} {...(props as object)} />
  );
}

/** La cabecera de una pantalla: antetítulo, título, explicación y acciones. */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <m.header
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 0.8, 0.28, 1] }}
      className="mb-7 flex flex-wrap items-end justify-between gap-6"
    >
      <div className="min-w-0">
        {eyebrow ? (
          <p className="mb-2 text-[0.68rem] font-extrabold tracking-[0.14em] text-primary uppercase">{eyebrow}</p>
        ) : null}
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
        {description ? <p className="mt-2 max-w-2xl text-sm text-ink-soft">{description}</p> : null}
      </div>

      {/*
        Sin `shrink-0`.

        Lo tenía, y con él el bloque de acciones no podía encogerse por debajo
        de su ancho natural: tres botones con etiquetas largas —«Empezar en
        blanco», «Gestionar plantillas», «Contratar desde una plantilla»— medían
        565 píxeles y desbordaban a lo ancho la página entera en un móvil. El
        `flex-wrap` de aquí dentro ya sabe repartirlos en varias líneas; lo
        único que hacía falta era dejarle sitio para hacerlo.
      */}
      {actions ? <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </m.header>
  );
}

/** Un bloque con título dentro de una pantalla. */
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-4', className)}>
      {title || actions ? (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {title ? <h2 className="text-base font-semibold text-ink">{title}</h2> : null}
            {description ? <p className="mt-1 text-sm text-ink-soft">{description}</p> : null}
          </div>
          {actions ? <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}

      {children}
    </section>
  );
}
